import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { CheckCircle, XCircle, Loader2, Ship } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const VerifyEmail = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('verifying'); // verifying, success, error
  const [message, setMessage] = useState('Verifying your email...');
  const hasVerified = useRef(false);

  useEffect(() => {
    const token = searchParams.get('token');
    
    if (!token) {
      setStatus('error');
      setMessage('No verification token provided');
      return;
    }

    // Prevent double verification calls (React strict mode)
    if (hasVerified.current) return;
    hasVerified.current = true;

    verifyEmail(token);
  }, [searchParams]);

  const verifyEmail = async (token) => {
    try {
      const response = await axios.get(`${API_URL}/auth/verify-email/${token}`);
      setStatus('success');
      setMessage(response.data.message || 'Email verified successfully! You can now log in.');
    } catch (error) {
      setStatus('error');
      setMessage(error.response?.data?.detail || 'Verification failed. The link may be expired or invalid.');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-ocean-900 via-ocean-800 to-cyan-900 flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="flex items-center justify-center gap-2 mb-4">
            <img 
              src="/logo-icon.png" 
              alt="CTC Logo" 
              className="h-10 w-10 object-contain rounded-lg"
            />
            <span className="text-2xl font-outfit font-bold">
              <span className="text-cyan-500">Connecting</span> The Caribbean
            </span>
          </div>
          <CardTitle className="text-2xl">Email Verification</CardTitle>
        </CardHeader>
        <CardContent className="text-center">
          {status === 'verifying' && (
            <div className="py-8">
              <Loader2 className="h-16 w-16 mx-auto mb-4 text-cyan-500 animate-spin" />
              <p className="text-slate-600">{message}</p>
            </div>
          )}

          {status === 'success' && (
            <div className="py-8">
              <CheckCircle className="h-16 w-16 mx-auto mb-4 text-green-500" />
              <h3 className="text-xl font-semibold text-green-700 mb-2">Success!</h3>
              <p className="text-slate-600 mb-6">{message}</p>
              <Button
                onClick={() => navigate('/login')}
                className="bg-cyan-500 hover:bg-cyan-600 w-full"
                data-testid="go-to-login-button"
              >
                Go to Login
              </Button>
            </div>
          )}

          {status === 'error' && (
            <div className="py-8">
              <XCircle className="h-16 w-16 mx-auto mb-4 text-red-500" />
              <h3 className="text-xl font-semibold text-red-700 mb-2">Verification Failed</h3>
              <p className="text-slate-600 mb-6">{message}</p>
              <div className="space-y-3">
                <Button
                  onClick={() => navigate('/register')}
                  variant="outline"
                  className="w-full"
                >
                  Register Again
                </Button>
                <Button
                  onClick={() => navigate('/login')}
                  className="bg-cyan-500 hover:bg-cyan-600 w-full"
                >
                  Try to Login
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default VerifyEmail;
